import { CurationItem } from "../../types";
import { callCurationProxy } from "../backend/backendClient";
import { getPublisherId } from "../auth/authClient";

/**
 * Creates a new page
 * @param parentId
 * @param pageTitle
 * @param pageAlias
 * @returns {Promise<CurationItem>}
 */
export const createPage = async (
    parentId: string,
    pageTitle: string,
    pageAlias: string,
): Promise<CurationItem> => {
    if (!parentId || !pageTitle || !pageAlias) {
        throw new Error("createPage requires a parentId, pageTitle, and pageAlias.");
    }
    const publisherId = getPublisherId();

    if (!publisherId) {
        throw new Error(
            "Publisher ID is missing from authentication cookies. Please log in again.",
        );
    }

    const createBody = [
        {
            contentType: "page",
            parentId: parentId,
            metadata: {
                default: { "publisher-id": publisherId },
                en_US: {},
            },
        },
    ];

    const createRes = await callCurationProxy({
        path: "/list/?index=0&copyImagePack=true",
        method: "POST",
        body: createBody,
    });

    const createResponseText = await createRes.text();
    if (!createRes.ok) {
        throw new Error(`POST Error for ${pageTitle}: ${createRes.status}, ${createResponseText}`);
    }

    const creationData = JSON.parse(createResponseText);
    const newPage = creationData.result?.[0];

    if (!newPage?.id) {
        throw new Error(
            `API Error: Page ${pageTitle} created but no ID was returned. Response: ${createResponseText}`,
        );
    }

    const updateBody = {
        ...newPage,
        title: pageTitle,
        metadata: {
            ...newPage.metadata,
            default: {
                ...newPage.metadata.default,
                "page-slug": pageAlias,
            },
            en_US: {
                ...newPage.metadata.en_US,
                title: pageTitle,
            },
        },
    };

    const updateRes = await callCurationProxy({
        path: `/list/${newPage.id}`,
        method: "PUT",
        body: updateBody,
    });

    const updateResponseText = await updateRes.text();
    if (!updateRes.ok) {
        throw new Error(`PUT Error for ${pageTitle}: ${updateRes.status}, ${updateResponseText}`);
    }

    return newPage;
};
